import { useState, useEffect } from "react";
import { Button, Table, Popconfirm } from "@/frontend/components/common";
import { subCategoryService } from "@/frontend/services";
import type { PaginationRequestDTO, SubCategoryResponseDTO } from "@/shared/dto";
import { formatDate } from "@/shared/utils";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { usePagination, useDelete } from "@/frontend/hooks";
import { Select, DatePicker, Input } from "antd";

const { RangePicker } = DatePicker;

const initialPagination: TablePaginationConfig = {
  pageSizeOptions: ["7","10", "20", "50", "100"],
  showSizeChanger: true,
  showTotal: (total) => `Total: ${total} registros`,
};

const initialParams: PaginationRequestDTO = { page: 1, pageSize: 7 };

export function SubCategoriesListPage() {
  const navigate = useNavigate();
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [filterDisciplineId, setFilterDisciplineId] = useState<string | undefined>();
  const [disciplines, setDisciplines] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    subCategoryService.getPaginate({ page: 1, pageSize: 100 }).then((res) => {
      if (res.success && res.data) {
        const seen = new Map<string, string>();
        for (const s of res.data.data) {
          if (s.disciplineId && s.disciplineName && !seen.has(s.disciplineId)) {
            seen.set(s.disciplineId, s.disciplineName);
          }
        }
        setDisciplines([...seen.entries()].map(([id, name]) => ({ id, name })));
      }
    });
  }, []);

  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  function applyFilters(category = filterCategory, disciplineId = filterDisciplineId, range = dateRange) {
    const trimmedCategory = category.trim();

    loadData({
      page: 1,
      pageSize: 7,
      category: trimmedCategory || undefined,
      disciplineId,
      createdFrom: range?.[0],
      createdTo: range?.[1],
    });
  }

  const { data, loading, pagination, loadData } = usePagination<SubCategoryResponseDTO>({
    fetchFn: subCategoryService.getPaginate,
    initialParams,
    initialPagination,
  });

  const { handleDelete, showConfirm, confirmTitle, confirmDescription } = useDelete({
    deleteFn: subCategoryService.delete,
    loadData,
    successMessage: "Subcategoría eliminada exitosamente",
    errorMessage: "Error al eliminar subcategoría",
    showConfirm: true,
    confirmTitle: "¿Eliminar subcategoría?",
    confirmDescription: "Esta acción eliminará la subcategoría permanentemente. ¿Estás seguro?",
  });

  const columns: ColumnsType<SubCategoryResponseDTO> = [
    {
      title: "Deporte / Disciplina",
      dataIndex: "disciplineName",
      key: "disciplineName",
    },
    {
      title: "Categoría",
      dataIndex: "category",
      key: "category",
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: (desc: string | null) => desc || "-",
    },
    {
      title: "Fecha de Creación",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => formatDate(date),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_: unknown, record: SubCategoryResponseDTO) => (
        <div className="flex gap-2">
          <Button
            type="link"
            icon={<FaEdit />}
            onClick={() => navigate(`/sub-categories/edit/${record.id}`)}
          >
            Editar
          </Button>
          {showConfirm ? (
            <Popconfirm
              title={confirmTitle}
              description={confirmDescription}
              onConfirm={() => handleDelete(record.id)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger icon={<FaTrash />}>
                Eliminar
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="link"
              danger
              icon={<FaTrash />}
              onClick={() => handleDelete(record.id)}
            >
              Eliminar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Gestión de Subcategorías</h2>
          <p className="text-gray-600 mt-1">
            Administra las subcategorías vinculadas a cada disciplina deportiva
          </p>
        </div>
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => navigate("/sub-categories/create")}
        >
          Crear Subcategoría
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <Input.Search
          placeholder="Buscar categoría por nombre"
          allowClear
          value={filterCategory}
          onChange={(event) => {
            const value = event.target.value;
            setFilterCategory(value);
            if (!value) applyFilters("", filterDisciplineId, dateRange);
          }}
          onSearch={(value) => {
            setFilterCategory(value);
            applyFilters(value, filterDisciplineId, dateRange);
          }}
          style={{ width: 260 }}
        />
        <Select
          placeholder="Deporte / Disciplina"
          allowClear
          showSearch
          value={filterDisciplineId}
          onChange={(val) => { setFilterDisciplineId(val); applyFilters(filterCategory, val, dateRange); }}
          options={disciplines.map((d) => ({ label: d.name, value: d.id }))}
          style={{ width: 220 }}
          filterOption={(input, opt) =>
            String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
        <RangePicker
          placeholder={["Fecha inicio", "Fecha fin"]}
          onChange={(_, dateStrings) => {
            const range = dateStrings && dateStrings[0] && dateStrings[1]
              ? [dateStrings[0], dateStrings[1]] as [string, string]
              : null;
            setDateRange(range);
            applyFilters(filterCategory, filterDisciplineId, range);
          }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={pagination}
      />
    </div>
  );
}
